import useFigmaBetCartModel from '../_hooks/useFigmaBetCartModel';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { X, Trash2, ShoppingCart, Eraser, Plus, Minus } from 'lucide-react';

import { formatOdds } from '../_helpers/oddsFormat';

import { getMarketTitle } from './marketTitleResolver';
import { getSelectionLabel } from './selectionLabelResolver';

import BonusRecoveryModal from '../../../components/CommonModal/BonusRecoveryModal';
import SportsBetSuccessModal from '../../../components/CommonModal/SportsBetSuccessModal';

const fmt = n => Number(n).toLocaleString('ko-KR');


const FigmaBetCart = ({
  isMobile = false,
  mobileOpen = false,
  presentationActive = true,
  onMobileClose = () => {},
  onMobilePresentationBusyChange,
}) => {
  const navigate = useNavigate();
  const {
    _fmt,
    addAmt,
    availableBalance,
    balanceLoading,
    bettingLimitsLoading,
    bonusActionType,
    bonusEligibleLegCount,
    bonusGames,
    bonusTierLegCount,
    cartChangeSummary,
    clearCart,
    closeBonusRecoveryModal,
    comboAdjustText,
    comboOddsAdjust,
    comboPreviewUnresolved,
    displayedBaseOdds,
    displayedBonusMultiplier,
    displayedFinalOdds,
    displayedPotentialWin,
    effectiveMinimumStake,
    handleAcceptCartChanges,
    handleBonusRecoveryConfirm,
    handleDecrement,
    handleIncrement,
    handlePlace,
    maxStakeInfo,
    mobileHeadingRef,
    oddsChanges,
    pendingBetState,
    presentationBusy,
    quoteErrorCode,
    quoteErrorMessage,
    quoteLoading,
    removeSelection,
    requestMobileClose,
    selections,
    setStake,
    showBonusRecoveryModal,
    showBetSuccessModal,
    closeBetSuccessModal,
    stake,
    submitting,
  } = useFigmaBetCartModel({ isMobile, mobileOpen, presentationActive, onMobileClose, onMobilePresentationBusyChange });

  const cartPanel = (
    <div className={`zxsb_cart_wrap ${isMobile ? 'zxsb_cart_wrap_mobile' : ''}`}
      inert={presentationBusy || !presentationActive ? '' : undefined} aria-busy={presentationBusy || undefined}>
      {/* ── Zone 1: Tabs ── */}
      <div className="zxsb_cart_tabs">
        <button type="button" className="zxsb_cart_tab zxsb_cart_tab_active">
          베팅카트 {selections.length > 0 && <span className="zxsb_cart_badge">{selections.length}</span>}
        </button>
        <button type="button" className="zxsb_cart_tab" onClick={() => navigate('/betting-history?tab=Sportsbook')}>
          내 베팅
        </button>
      </div>

      {/* ── Zone 2: Summary + Items ── */}
      <div className="zxsb_cart_body">
        <div className="zxsb_cart_sumbar">
          <span>선택 목록 ({selections.length})</span>
        </div>

        <div className="zxsb_cart_items">
          {selections.length === 0 ? (
            <div className="zxsb_cart_empty">
              <ShoppingCart size={28} strokeWidth={1.5} />
              <span>베팅 카트가 비어있습니다</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>배당을 클릭하여 추가하세요</span>
            </div>
          ) : (
            selections.map(sel => {
              const isLocked = sel.status === 'locked';
              const isUnavailable = sel.status === 'unavailable';
              const isOddsChanged = sel.status === 'odds_changed';
              return (
              <div key={sel.odds_id} className="zxsb_cart_card" style={(isLocked || isUnavailable) ? { opacity: 0.55 } : undefined}>
                <div className="zxsb_cart_card_top">
                  <span className="zxsb_cart_card_match">{sel.match_name}</span>
                  <button type="button" className="zxsb_cart_card_x" disabled={submitting} onClick={() => removeSelection(sel.odds_id)}><X size={12} /></button>
                </div>
                <div className="zxsb_cart_card_mid">
                  {sel.market_title_display_ko
                    || sel.market_name_ko
                    || getMarketTitle(sel.market_title_display)
                    || getMarketTitle(sel.market_name)
                    || sel.market_title_display
                    || sel.market_name}
                  {sel.market_line ? ` (${String(sel.market_line).replace(/\s*\(0-0\)\s*$/, '')})` : ''}
                </div>
                <div className="zxsb_cart_card_bot">
                  <span className="zxsb_cart_card_sel">{
                    sel.selection_name_display
                      || getSelectionLabel(
                        { name: sel.selection_name },
                        { marketName: sel.market_title_display_ko || sel.market_name_ko || sel.market_title_display || sel.market_name, match: sel }
                      )
                  }</span>
                  <span className={`zxsb_cart_card_odds ${oddsChanges?.[sel.odds_id] === 'up' ? 'zxsb_delta_up' : oddsChanges?.[sel.odds_id] === 'down' ? 'zxsb_delta_down' : ''}`}>
                    {formatOdds(sel.odds, _fmt)}
                  </span>
                </div>
                {/* Persistent status overlay — never auto-applied. Resolution is
                    performed atomically by the single footer action. */}
                {(isLocked || isUnavailable || isOddsChanged) && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      marginTop: 6, padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      background: isOddsChanged ? 'rgba(89, 244, 119, 0.12)' : 'rgba(166, 196, 213, 0.12)',
                      color: isUnavailable ? '#ef4444' : (isLocked ? '#a6c4d5' : 'var(--sb-primary)'),
                    }}
                  >
                    <span>
                      {isUnavailable && '이용 불가'}
                      {isLocked && !isUnavailable && '배당 잠김'}
                      {isOddsChanged && `배당 변경: ${formatOdds(sel.odds, _fmt)} → ${formatOdds(sel.pendingOdds, _fmt)}`}
                    </span>
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Zone 3: Footer (sticky bottom) ── */}
      <div className="zxsb_cart_foot">
        {/* Secondary Info Rows */}
        <div className="zxsb_cart_info_group_secondary">
          <div className="zxsb_cart_frow_sec">
            <span>보유금</span>
            <span>{balanceLoading && availableBalance <= 0 ? '조회중' : fmt(availableBalance || 0)}원</span>
          </div>
          {selections.length > 0 && (
            <>
              <div className="zxsb_cart_frow_sec">
                <span>합산배당</span>
                <span>{formatOdds(displayedBaseOdds, _fmt)}x</span>
              </div>
              <div className="zxsb_cart_frow_sec">
                <span>
                  보너스배당
                  {bonusEligibleLegCount != null && ` (인정 ${bonusEligibleLegCount}폴)`}
                </span>
                <span
                  className={quoteErrorCode ? 'zxsb_cart_bonus_error' : undefined}
                  title={quoteErrorMessage || undefined}
                  style={{ color: displayedBonusMultiplier > 1 ? '#e5a913' : 'inherit', fontWeight: displayedBonusMultiplier > 1 ? 700 : 'normal' }}
                >
                  {quoteLoading
                    ? '확인중'
                    : quoteErrorCode
                    ? quoteErrorMessage
                    : `x${Number(displayedBonusMultiplier).toFixed(2)}`}
                  {!quoteLoading && !quoteErrorCode
                    && bonusTierLegCount != null && displayedBonusMultiplier > 1
                    && ` / ${bonusTierLegCount}폴`}
                </span>
              </div>
              {Math.abs(comboOddsAdjust) > 0.001 && (
                <div className="zxsb_cart_frow_sec">
                  <span>조합배당조정</span>
                  <span style={{ color: comboOddsAdjust > 0 ? 'var(--sb-primary)' : '#ef4444' }}>
                    {comboAdjustText}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* 모두비우기 */}
        <div className="zxsb_cart_clear_row">
          <button type="button" className="zxsb_cart_clear_all_btn" disabled={submitting} onClick={clearCart}>
            <Trash2 size={16} className="zxsb_cart_clear_all_icon" />
            <span>모두비우기</span>
          </button>
        </div>

        {/* + / - 베팅금액 Eraser */}
        <div className="zxsb_cart_stake_row_new">
          <div className="zxsb_cart_adjust_group">
            <button type="button" className="zxsb_cart_adjust_btn" disabled={submitting} onClick={handleIncrement}>
              <Plus size={14} />
            </button>
            <button type="button" className="zxsb_cart_adjust_btn" disabled={submitting} onClick={handleDecrement}>
              <Minus size={14} />
            </button>
          </div>
          <div className="zxsb_cart_input_wrapper">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9,]*"
              className="zxsb_cart_amount_input"
              placeholder="베팅금액"
              disabled={submitting}
              value={stake ? fmt(Number(stake)) : ''}
              onChange={e => { const raw = e.target.value.replace(/[^0-9]/g, ''); setStake(raw); }}
            />
            <button type="button" className="zxsb_cart_input_clear" disabled={submitting} onClick={() => setStake('')}>
              <Eraser size={16} />
            </button>
          </div>
        </div>

        {/* Preset Buttons */}
        <div className="zxsb_cart_presets">
          <button type="button" className="zxsb_cart_preset_btn" disabled={submitting} onClick={() => addAmt(10000)}>1만</button>
          <button type="button" className="zxsb_cart_preset_btn" disabled={submitting} onClick={() => addAmt(50000)}>5만</button>
          <button type="button" className="zxsb_cart_preset_btn" disabled={submitting} onClick={() => addAmt(100000)}>10만</button>
          <button type="button" className="zxsb_cart_preset_btn" disabled={submitting} onClick={() => addAmt(300000)}>30만</button>
          <button type="button" className="zxsb_cart_preset_btn" disabled={submitting} onClick={() => addAmt(500000)}>50만</button>
          <button type="button" className="zxsb_cart_preset_btn" disabled={submitting} onClick={() => addAmt('max')}>MAX</button>
        </div>

        {/* Primary Info Rows */}
        <div className="zxsb_cart_info_rows_primary">
          <div className="zxsb_cart_info_row">
            <span className="zxsb_cart_info_label">전체 배당</span>
            <span className="zxsb_cart_info_value">x{formatOdds(displayedFinalOdds || 1.00, _fmt)}</span>
          </div>
          <div className="zxsb_cart_info_row">
            <span className="zxsb_cart_info_label">예상 당첨금액</span>
            <span className="zxsb_cart_info_value">{fmt(Math.floor(displayedPotentialWin || 0))}</span>
          </div>
        </div>

        {selections.length > 0 && (
          <div style={{ marginTop: 4, marginBottom: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
            {bettingLimitsLoading || balanceLoading
              ? '최대 가능 금액 계산 중...'
              : maxStakeInfo.maxStake >= effectiveMinimumStake
              ? `최대 가능 금액: ${fmt(maxStakeInfo.maxStake)}원`
              : `최소 베팅액 ${fmt(effectiveMinimumStake)}원 미만 베팅 불가`}
          </div>
        )}

        {cartChangeSummary.actionableCount > 0 && (
          <div className="zxsb_cart_accept_changes">
            <div className="zxsb_cart_accept_changes_summary">
              {cartChangeSummary.changedCount > 0 && (
                <span>변경 {cartChangeSummary.changedCount}개</span>
              )}
              {cartChangeSummary.removedCount > 0 && (
                <span>제외 {cartChangeSummary.removedCount}개</span>
              )}
            </div>
            <button
              type="button"
              className="zxsb_cart_accept_changes_btn"
              disabled={submitting}
              onClick={handleAcceptCartChanges}
            >
              변동사항 수락
            </button>
          </div>
        )}

        {/* 베팅하기 CTA Button */}
        <button
          type="button"
          className="zxsb_cart_bet_btn"
          onClick={handlePlace}
          disabled={submitting || selections.length === 0 || !(Number(stake) > 0) || comboPreviewUnresolved}
        >
          {submitting ? '처리중...' : '베팅하기'}
        </button>
      </div>
    </div>
  );

  const pendingOverlay = pendingBetState ? (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 6110,
      background: 'rgba(3, 14, 19, 0.82)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '360px',
        borderRadius: '18px',
        background: '#142d37',
        border: '1px solid rgba(166, 196, 213, 0.18)',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.48)',
        padding: '24px 22px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#f3f8fa' }}>
          인플레이 베팅 확인 중
        </div>
        <div style={{
          marginTop: '14px',
          fontSize: '34px',
          fontWeight: 900,
          color: 'var(--sb-primary)',
          lineHeight: 1,
        }}>
          {pendingBetState.secondsLeft > 0 ? pendingBetState.secondsLeft : '...'}
        </div>
        <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 700, color: '#e7f0f4' }}>
          배당변동 확인중
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.5, color: '#a6c4d5' }}>
          {pendingBetState.secondsLeft > 0
            ? `${pendingBetState.secondsLeft}초 동안 배당과 마켓 상태를 다시 확인합니다.`
            : '최종 베팅 결과를 확인하고 있습니다.'}
        </div>
        <div style={{ marginTop: '12px', fontSize: '12px', lineHeight: 1.5, color: '#7894a0' }}>
          배당이 변동되거나 마켓이 닫히면 베팅이 거부될 수 있습니다.
        </div>
      </div>
    </div>
  ) : null;

  // The cart snapshot is immutable once submission starts. This transparent
  // interaction fence also blocks fixture-list selection changes outside the
  // cart while the authoritative preflight and placement use that snapshot.
  const submissionInteractionLock = submitting && !pendingBetState ? (
    <div
      aria-label="최종 베팅 조건 확인 중"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 6100,
        background: 'transparent',
        cursor: 'wait',
      }}
    />
  ) : null;

  if (!isMobile) {
    return (
      <>
        {cartPanel}
        {submissionInteractionLock}
        {pendingOverlay}
        <BonusRecoveryModal
          show={presentationActive && showBonusRecoveryModal}
          onHide={closeBonusRecoveryModal}
          onConfirm={handleBonusRecoveryConfirm}
          onCancel={closeBonusRecoveryModal}
          gameType="sport"
          bonusgames={bonusGames}
          actionType={bonusActionType}
        />
        <SportsBetSuccessModal
          show={presentationActive && showBetSuccessModal}
          onClose={closeBetSuccessModal}
        />
      </>
    );
  }

  if (typeof document === 'undefined') {
    return pendingOverlay;
  }

  return createPortal(
    <div className="zxsb_mobile_cart_portal_root sportsbook-theme" inert={!presentationActive ? '' : undefined} aria-hidden={!presentationActive || undefined}>
      <div
        className={`zxsb_mobile_cart_scrim ${mobileOpen ? 'zxsb_mobile_cart_scrim_open' : ''}`}
        onClick={requestMobileClose}
      />
      <div className={`zxsb_mobile_cart_sheet ${mobileOpen ? 'zxsb_mobile_cart_sheet_open' : ''}`}
        onKeyDown={isMobile && mobileOpen && presentationActive ? event => {
          if (event.key !== 'Escape' || event.defaultPrevented || event.nativeEvent?.isComposing
            || !event.currentTarget.contains(event.target)) return;
          event.preventDefault();
          event.stopPropagation();
          requestMobileClose();
        } : undefined}
      >
        <div className="zxsb_mobile_cart_handle" />
        <div className="zxsb_mobile_cart_head">
          <strong ref={mobileHeadingRef} tabIndex={-1}>베팅카트</strong>
          <button type="button" className="zxsb_mobile_cart_close" onClick={requestMobileClose} disabled={presentationBusy} aria-label="베팅카트 닫기">
            <X size={18} />
          </button>
        </div>
        {cartPanel}
      </div>
      {submissionInteractionLock}
      {pendingOverlay}
      <BonusRecoveryModal
        show={presentationActive && showBonusRecoveryModal}
        onHide={closeBonusRecoveryModal}
        onConfirm={handleBonusRecoveryConfirm}
        onCancel={closeBonusRecoveryModal}
        gameType="sport"
        bonusgames={bonusGames}
        actionType={bonusActionType}
      />
      <SportsBetSuccessModal
        show={presentationActive && showBetSuccessModal}
        onClose={closeBetSuccessModal}
      />
    </div>,
    document.body
  );
};

export default FigmaBetCart;
